import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionHeading, Counter } from "../components/Motion";
import { useContent, PROCESS } from "../lib/content";

const ProjectCard = ({ p, i }) => {
  const detailLink = p.title?.startsWith("Enterprise ROI") ? "/projects/roi-toolkit"
    : p.title?.startsWith("AI-Powered Solution Architecture") ? "/projects/enterprise-ai-analytics"
    : p.title?.startsWith("Executive Analytics") ? "/projects/google-executive-analytics"
    : null;
  const thumbSrc = detailLink === "/projects/roi-toolkit" ? "/thumbs/roi-toolkit.svg"
    : detailLink === "/projects/enterprise-ai-analytics" ? "/thumbs/enterprise-ai-analytics.svg"
    : detailLink === "/projects/google-executive-analytics" ? "/thumbs/google-executive-analytics.svg"
    : null;
  return (
  <Reveal delay={(i % 2) * 0.1}>
    <article
      data-testid={`project-card-${i}`}
      className="card-hover group rounded-2xl border border-border bg-card p-8 md:p-10 h-full hover:border-secondary/40 relative overflow-hidden"
    >
      <div className="mesh-glow w-64 h-64 bg-violet-600/10 -top-20 -right-20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="relative">
        <div className="flex items-start justify-between gap-4">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-secondary">{p.category}</p>
          <span className="font-mono text-xs text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
        </div>
        <h3 className="mt-4 font-display text-xl md:text-2xl font-medium tracking-tight leading-snug">{p.title}</h3>

        {thumbSrc && (
          <div className="mt-6 rounded-xl border border-border overflow-hidden bg-navy-950" data-testid={`project-thumb-${i}`}>
            <img src={thumbSrc} alt="" aria-hidden="true" loading="lazy"
              className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]" />
          </div>
        )}

        <dl className="mt-8 space-y-5 text-sm leading-relaxed">
          {[["Problem", p.problem], ["Approach", p.approach], ["Outcome", p.outcome]].map(([label, value]) => (
            <div key={label}>
              <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5">{label}</dt>
              <dd className="text-foreground/80">{value}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-8 flex flex-wrap gap-2">
          {(p.technology || []).map((t) => (
            <span key={t} className="px-3 py-1 rounded-full border border-border text-xs text-muted-foreground">{t}</span>
          ))}
        </div>

        <div className="mt-8 pt-6 border-t border-border">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5">Business Impact</p>
          <p className="font-display text-base md:text-lg font-medium text-gradient">{p.impact}</p>
          {detailLink && (
            <Link to={detailLink} data-testid="project-detail-link" className="mt-4 inline-flex items-center gap-2 text-sm text-secondary hover:underline">
              Read the full case study <ArrowUpRight size={14} />
            </Link>
          )}
        </div>
      </div>
    </article>
  </Reveal>
  );
};

export const ProjectsSection = ({ full = false, hideHeading = false }) => {
  const { projects } = useContent();
  const list = full ? projects : projects.filter((p) => p.featured);

  return (
    <section data-testid="projects-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
      {!hideHeading && (
        <SectionHeading
          overline="Featured Projects"
          title="Case studies in measurable transformation"
          description="Real engagements across enterprise AI, analytics, and automation - each anchored in a business problem and closed with quantified impact."
        />
      )}
      <div className="grid md:grid-cols-2 gap-6 md:gap-8">
        {list.map((p, i) => <ProjectCard key={p.id} p={p} i={i} />)}
      </div>
      {!full && (
        <Reveal className="mt-14">
          <Link to="/projects" data-testid="projects-more-link" className="inline-flex items-center gap-2 text-sm text-secondary hover:underline">
            View all case studies <ArrowUpRight size={14} />
          </Link>
        </Reveal>
      )}
    </section>
  );
};

export const ProcessSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 60%"] });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section data-testid="process-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
      <SectionHeading
        overline="Consulting Process"
        title="A disciplined path from ambiguity to outcomes"
        description="Six stages. Clear deliverables at every step. No black boxes."
      />
      <div ref={ref} className="relative pl-10 md:pl-14">
        <div className="absolute left-3 md:left-5 top-0 bottom-0 w-px bg-border" />
        <motion.div style={{ height: lineHeight }} className="absolute left-3 md:left-5 top-0 w-px bg-gradient-to-b from-violet-500 to-blue-500" />
        <div className="space-y-14 md:space-y-16">
          {PROCESS.map((s, i) => (
            <Reveal key={s.step} delay={i * 0.05}>
              <div data-testid={`process-step-${i}`} className="relative">
                <span className="absolute -left-[35px] md:-left-[45px] top-0.5 h-5 w-5 rounded-full border-2 border-secondary bg-background flex items-center justify-center">
                  <span className="h-1.5 w-1.5 rounded-full bg-secondary" />
                </span>
                <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-8">
                  <span className="font-mono text-sm text-secondary shrink-0 w-8">{s.step}</span>
                  <div>
                    <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight">{s.title}</h3>
                    <p className="mt-2 text-sm md:text-base text-muted-foreground leading-relaxed max-w-2xl">{s.detail}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const MetricsSection = () => {
  const { metrics } = useContent();
  return (
    <section data-testid="metrics-section" className="border-y border-border bg-muted/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-24 grid grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8">
        {metrics.map((m, i) => (
          <Reveal key={m.id} delay={i * 0.08}>
            <div data-testid={`metric-${i}`}>
              <p className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter text-gradient">
                <Counter value={m.value} suffix={m.suffix} />
              </p>
              <p className="mt-3 font-display font-medium text-sm md:text-base">{m.label}</p>
              <p className="mt-1 text-xs md:text-sm text-muted-foreground">{m.detail}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};
